import { PlusOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Typography,
  Upload,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { UploadFile } from 'antd/es/upload/interface';
import React, { useEffect } from 'react';

import useService from '@/lib/hooks/useService';
import type { Uploads } from '@/types/database/uploads';

import { JanitorKind, MemberReservationLimitKind } from '../../constants';
import sharedStyles from '../../sharedStyles.module.scss';
import type { CleanKindFetchResponse } from '../../sharedTypes';
import type { InformationFormDataType } from '../types';
import { customRequest, limitRuleUnits, setFormValues } from './helper';
import styles from './index.module.scss';

type CleanKindInformationProps = {
  form: FormInstance<InformationFormDataType>;
  planOptions: { label: string; value: string }[] | undefined;
  updatePicture: (newPicture: UploadFile<Uploads>) => void;
  type: 'add' | 'edit';
  cleanKindData?: CleanKindFetchResponse;
};

const { Item } = Form;
const { Text } = Typography;
const { TextArea } = Input;

const CleanKindInformation = ({
  form,
  planOptions,
  updatePicture,
  type,
  cleanKindData,
}: CleanKindInformationProps) => {
  const janitor = Form.useWatch('janitor', form);
  const reservationLimitRule = Form.useWatch('reservationLimitRule', form);

  const [uploads] = useService<Uploads>({
    collectionName: 'uploads',
  });

  useEffect(() => {
    let isMounted = true;
    if (type === 'edit' && cleanKindData && isMounted) {
      setFormValues({ cleanKindData, uploads, form });
    }
    return () => {
      isMounted = false;
    };
  }, [cleanKindData, form, type, uploads]);

  return (
    <div className={styles.formContentContainer}>
      <div>
        <Item
          label={<Text type="secondary">服務類別</Text>}
          name="cleanKindName"
          rules={[{ required: true, message: '請輸入服務項目名稱' }]}
        >
          <Input placeholder="請輸入服務項目名稱" />
        </Item>
        <Item
          label={<Text type="secondary">所屬計費方案</Text>}
          name="subscriptionPlanIdList"
          rules={[{ required: true, message: '請選擇所屬計費方案' }]}
        >
          <Select
            options={planOptions}
            placeholder="請選擇所屬計費方案"
            mode="multiple"
            showArrow
            showSearch={false}
            allowClear
          />
        </Item>
        <Item label={<Text type="secondary">服務介紹</Text>} name="description">
          <TextArea
            rows={4}
            placeholder="例如：一般住家四大區域重點式清潔服務，維護您每日... ..."
          />
        </Item>
        <Item label={<Text type="secondary">費用包含</Text>} name="included">
          <TextArea rows={4} placeholder="例如：費用已涵蓋到府評估... ..." />
        </Item>
        <Item label={<Text type="secondary">注意事項</Text>} name="warning">
          <TextArea
            rows={4}
            placeholder="例如：預約日請留意您的手機推播通知... ..."
          />
        </Item>
        <Item
          label={<Text type="secondary">上傳圖片</Text>}
          name="picture"
          valuePropName="fileList"
          getValueFromEvent={({ fileList }) => fileList}
        >
          <Upload
            listType="picture-card"
            maxCount={1}
            customRequest={(options) => customRequest(options, updatePicture)}
          >
            <div>
              <PlusOutlined />
              <div className={styles['mt-8']}>Upload</div>
            </div>
          </Upload>
        </Item>
      </div>
      <div>
        <Item>
          <Item
            label={<Text type="secondary">清潔人員</Text>}
            name="janitor"
            rules={[{ required: true, message: '請選擇其中一種' }]}
          >
            <Radio.Group>
              <Radio value={JanitorKind.Contractor}>約聘人員（一般清潔）</Radio>
              <Radio value={JanitorKind.Partner}>
                合作廠商人員（合作廠商清潔）
              </Radio>
            </Radio.Group>
          </Item>
          <Item
            label={<Text type="secondary">合作廠商</Text>}
            name="partnerName"
            dependencies={['janitor']}
            rules={[
              {
                validator: (_rules, value) => {
                  if (janitor === 'Partner' && !value) {
                    return Promise.reject(new Error('請選擇合作廠商'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              options={[{ label: '和仁洗衣公司', value: '和仁洗衣公司' }]}
              placeholder="從選單選擇"
              disabled={janitor !== 'Partner'}
            />
          </Item>
        </Item>
        <Item
          label={<Text type="secondary">每日會員預約上限次數（預設值）</Text>}
          name="dailyReservationLimitDefault"
          rules={[
            { required: true, message: '請輸入每日會員預約上限次數（預設值）' },
          ]}
        >
          <InputNumber
            min={0}
            placeholder="請輸入每日會員預約上限次數（預設值）"
            className={sharedStyles.widthFull}
          />
        </Item>
        <Item
          label={<Text type="secondary">會員預約限制</Text>}
          name="reservationLimitRule"
          rules={[{ required: true, message: '請選擇其中一種' }]}
        >
          <Radio.Group className={styles.limitRuleRadioGroup}>
            <Radio value={MemberReservationLimitKind.None}>無限制</Radio>
            <Radio value={MemberReservationLimitKind.InTimeRange}>
              時間內預約次數限制
            </Radio>
          </Radio.Group>
        </Item>
        <div className={styles.limitRuleUnitContainer}>
          <span>會員預約之清潔日期為每</span>
          <Item
            name="limitRuleValue"
            dependencies={['reservationLimitRule']}
            rules={[
              {
                validator: (_rules, value) => {
                  if (
                    reservationLimitRule === 'InTimeRange' &&
                    typeof value !== 'number'
                  ) {
                    return Promise.reject(new Error('請輸入數字'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              placeholder="請輸入數字"
              className={styles.limitRuleInput}
              disabled={reservationLimitRule !== 'InTimeRange'}
            />
          </Item>
          <Item
            name="limitRuleUnit"
            className={styles.limitRuleInput}
            dependencies={['reservationLimitRule']}
            rules={[
              {
                validator: (_rules, value) => {
                  if (reservationLimitRule === 'InTimeRange' && !value) {
                    return Promise.reject(new Error('請選擇單位'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              options={limitRuleUnits}
              placeholder="請選擇單位"
              disabled={reservationLimitRule !== 'InTimeRange'}
            />
          </Item>
        </div>
        <span className={styles['ml-24']}>
          最多1次。（每月以1號/每週以週日/每日以午夜0點開始做計算）
        </span>
      </div>
    </div>
  );
};

export default CleanKindInformation;
